// The numbers the data layer keeps its promises with. Each one is a
// constant so a test can assert it; none of them is a business rule.

import { ASSESSMENT_IDS } from "../schema/types";

/**
 * Wait before each retry after a failed sync pass: 1 s, 5 s, 30 s, 5 min.
 * Fast at first because most failures are a tunnel or a lift; slower after
 * so a phone left offline all afternoon does not burn its battery.
 */
export const SYNC_BACKOFF_MS: readonly number[] = [1_000, 5_000, 30_000, 300_000];

/**
 * The cap on any wait. Past the schedule the syncer retries every 5 min for
 * as long as the app is open, and never waits longer: an answer must not
 * sit on the device for an hour because the schedule kept growing.
 */
export const SYNC_MAX_DELAY_MS = 300_000;

/**
 * Runs sent per pass, the bound of the sync loop. One account holds at most
 * one current run per assessment, so a single pass empties its outbox.
 */
export const OUTBOX_BATCH = ASSESSMENT_IDS.length;

/**
 * Operations one run may queue: a redo and the save or finish of the
 * version it opens, per version. An athlete redoing the same assessment
 * eight times without a network is not a real case; the cap keeps the
 * per-run loop of the syncer bounded by a constant.
 */
export const RUN_OUTBOX_MAX = 16;

/**
 * recordAnswer must persist within this budget (SPEC-25): the next screen
 * waits on it. Above it, the write logs a warning so a slow device shows
 * up in the logs instead of as a sluggish questionnaire.
 */
export const ANSWER_WRITE_BUDGET_MS = 50;

/**
 * How long logout waits for a last sync pass before it reports what is
 * still unsent. Long enough for a slow mobile round trip, short enough
 * that "Terminar sessão" never feels stuck.
 */
export const LOGOUT_SYNC_TIMEOUT_MS = 5_000;

/**
 * How long the hub waits for the server when the device holds nothing of
 * the account (a sign-in on a new phone): the skeletons stay up meanwhile,
 * so an account with answers never shows as new (SPEC-16). The screens'
 * "max 10 s" (SPEC-14, SPEC-15); past it, the empty state is all there is.
 */
export const FIRST_HUB_WAIT_MS = 10_000;

/**
 * The wait before retry number `attempt` (0 for the first retry). Never
 * above SYNC_MAX_DELAY_MS, whatever the attempt, including a negative or
 * fractional one.
 */
export function nextDelay(attempt: number): number {
  const index = Math.max(0, Math.floor(Number.isFinite(attempt) ? attempt : SYNC_BACKOFF_MS.length));
  const scheduled = SYNC_BACKOFF_MS[index] ?? SYNC_MAX_DELAY_MS;
  return Math.min(scheduled, SYNC_MAX_DELAY_MS);
}
